import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { orderBy, sortBy } from 'lodash-es';
import { CustomModal, Button, TableList, Page, Spinner, Typography, PageEmptyState, PageErrorState, PageContentHeader, useStateInRef, FilterPickerGroup, Searchbox, MultiSelectFilterPicker } from 'seagull';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import { ErrorText } from '~/constants';
import { useAccountingItems } from '~/hooks/reactQuery/queries/useAccountingItems';
import { useOnMount } from '~/hooks/useOnMount';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStore, useStoreActions } from '~/store/store-context';
import { AccountingStatus } from '~/types/api/accounting-integration';
import { filterServiceOnlyTemplates } from '~/utils/filterServiceOnlyTemplates';
import NoDataIcon from '../../assets/NoDataIcon';
import { getAccountingDetails } from '../accounting.utils';
import styles from './MapServicesModal.module.scss';
import { columnDefs, getSearchedServicesAndFilteredOptions, getHeaderItems, getNumMappedServices } from './MapServicesModal.utils';
import MapServicesRow from './MapServicesRow';
export function MapServicesModal(param) {
    let { dismissModal, onChange, serviceTemplateIds, accounting } = param;
    const { t: texts } = useLocaleKeys('integrations.accounting');
    const { t: commonTexts } = useLocaleKeys('common');
    const { items: accountingItems, isLoading: loadingAccountingItems, error: errorAccountingItems, refresh: refreshAccountingItems } = useAccountingItems(accounting);
    const businessId = useStore(selectors.user.businessId);
    const { api, logBI } = useStoreActions();
    const { inputDebounceTime } = useAppConfig();
    const [modalState, setModalState] = useState('loading');
    const [serviceTemplates, setServiceTemplates] = useState();
    const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
    const [errorServiceTemplates, setErrorServiceTemplates] = useState(false);
    const [search, setSearch] = useState('');
    const [serviceMappingStatusFilter, setServiceMappingStatusFilter] = useState([]);
    const serviceTemplateApi = useServiceTemplateApi();
    const isLoading = !accountingItems && loadingAccountingItems || !serviceTemplates && loadingServiceTemplates;
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const { testIdPrefix, biCodes, fullName } = getAccountingDetails(accounting);
    const getStateInRef = useStateInRef({
        serviceTemplates,
        errorAccountingItems,
        errorServiceTemplates,
        accountingItems,
        userId,
        vendorId
    });
    const { filteredServices, serviceFilterOptions } = useMemo(()=>getSearchedServicesAndFilteredOptions({
            services: serviceTemplates || [],
            search,
            statusFilters: serviceMappingStatusFilter,
            accounting,
            accountingItems: (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || []
        }), [
        serviceTemplates,
        search,
        serviceMappingStatusFilter,
        accounting,
        accountingItems
    ]);
    useOnMount(()=>{
        refreshData();
    });
    useEffect(()=>{
        const { serviceTemplates, errorAccountingItems, errorServiceTemplates, accountingItems, userId, vendorId } = getStateInRef();
        const hasError = errorAccountingItems || (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.status) === AccountingStatus.RequestTimeout || errorServiceTemplates;
        const noAnchorServiceTemplates = serviceTemplates && serviceTemplates.length === 0;
        const hasData = serviceTemplates && accountingItems;
        if (isLoading) setModalState('loading');
        else if (hasError) setModalState('error');
        else if (noAnchorServiceTemplates) setModalState('empty');
        else if (hasData) setModalState('valid');
        if (hasError) {
            logBI(biCodes.linkService, {
                vendorId,
                userId
            });
        }
    }, [
        biCodes.linkService,
        isLoading
    ]);
    async function fetchServiceTemplates() {
        setLoadingServiceTemplates(true);
        try {
            const services = await serviceTemplateApi.getForMappingToAccountingIntegration();
            const filteredServices = (serviceTemplateIds === null || serviceTemplateIds === void 0 ? void 0 : serviceTemplateIds.length) ? services.filter((s)=>serviceTemplateIds.includes(s.id)) : services;
            const orderedByName = orderBy(filteredServices, [
                'name'
            ], [
                'asc'
            ]);
            const sortedByIntegrationStatus = sortBy(orderedByName, (serviceTemplate)=>!!serviceTemplateModel.integratedId({
                    serviceTemplate,
                    accounting
                }));
            const sortedByDeletedStatus = sortBy(sortedByIntegrationStatus, serviceTemplateModel.isDeleted);
            setServiceTemplates(sortedByDeletedStatus);
        } catch (err) {
            setErrorServiceTemplates(true);
        } finally{
            setLoadingServiceTemplates(false);
        }
    }
    const numOfMappedServices = useMemo(()=>getNumMappedServices(serviceTemplates || [], accounting, (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || null), [
        accounting,
        serviceTemplates,
        accountingItems
    ]);
    const accountingItemsDictionary = useMemo(()=>(accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items.reduce((acc, item)=>_object_spread_props(_object_spread({}, acc), {
                [item.id]: item
            }), {})) || {}, [
        accountingItems
    ]);
    const serviceOnlyItems = useMemo(()=>filterServiceOnlyTemplates((accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || []), [
        accountingItems
    ]);
    async function refreshData() {
        fetchServiceTemplates();
        refreshAccountingItems();
    }
    async function onChangeMap(serviceTemplate, accountingItem) {
        const linkResponse = await handlePromise(api.accounting.linkServiceTemplate(_object_spread({
            accounting,
            serviceTemplateId: serviceTemplate.id
        }, accountingItem ? {
            integratedId: accountingItem.id,
            type: accountingItem.type
        } : {})));
        if (linkResponse.success) {
            onChange && onChange({
                serviceTemplateId: serviceTemplate.id,
                accountingItemId: accountingItem === null || accountingItem === void 0 ? void 0 : accountingItem.id
            });
            const updatedServiceTemplate = await handlePromise(api.serviceTemplate.getById(businessId, serviceTemplate.id));
            if (updatedServiceTemplate.success) {
                setServiceTemplates((prev)=>{
                    const newAnchorServiceTemplates = prev === null || prev === void 0 ? void 0 : prev.map((s)=>{
                        const isDesiredServiceTemplate = s.id == serviceTemplate.id;
                        return isDesiredServiceTemplate ? updatedServiceTemplate.data : s;
                    });
                    return newAnchorServiceTemplates;
                });
            } else {
                throw updatedServiceTemplate.error;
            }
        } else {
            throw linkResponse.error;
        }
    }
    return /*#__PURE__*/ _jsx(CustomModal, {
        bi: {
            closeButton: {
                name: 'map_services_modal_close'
            }
        },
        testId: "".concat(testIdPrefix, "-map-services-modal"),
        footer: modalState === 'valid' && /*#__PURE__*/ _jsxs("div", {
            className: styles.footer,
            children: [
                serviceTemplates && /*#__PURE__*/ _jsx("div", {
                    "data-testid": "total-map-status",
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.mapServicesFooter({
                            mapped: String(numOfMappedServices),
                            total: String(serviceTemplates.length)
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'close_map_services_modal'
                    },
                    onClick: dismissModal,
                    testId: "".concat(testIdPrefix, "-map-services-close"),
                    children: commonTexts.buttons.close()
                })
            ]
        }),
        height: "720px",
        minWidth: "1280px",
        onClose: dismissModal,
        children: /*#__PURE__*/ _jsx(Page, {
            height: "100%",
            header: texts.mapServicesHeader({
                fullName
            }),
            subheader: /*#__PURE__*/ _jsx(_Fragment, {
                children: texts.mapServicesSubheader({
                    fullName
                })
            }),
            skin: "secondary",
            testId: "".concat(testIdPrefix, "-map-services"),
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                columnDefs: columnDefs,
                children: [
                    modalState === 'loading' && /*#__PURE__*/ _jsx(LoadingState, {}),
                    modalState === 'empty' && /*#__PURE__*/ _jsx(EmptyState, {
                        accounting: accounting
                    }),
                    modalState === 'error' && /*#__PURE__*/ _jsx(ErrorState, {
                        onRefresh: refreshData
                    }),
                    modalState === 'valid' && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(PageContentHeader, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.filterContainer,
                                        children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                            flexWrap: true,
                                            testId: "map-services-modal-filters",
                                            children: [
                                                /*#__PURE__*/ _jsx(Searchbox, {
                                                    bi: {
                                                        name: 'map_services_search'
                                                    },
                                                    value: search,
                                                    testId: "map-services-modal-search",
                                                    placeholder: commonTexts.searchPlaceholder(),
                                                    debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                                                    onChange: (event)=>setSearch(event.target.value),
                                                    onClear: ()=>setSearch('')
                                                }),
                                                /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                                                    bi: {
                                                        name: 'accounting_service_mapping_status_filter'
                                                    },
                                                    testId: "".concat(testIdPrefix, "-mapping-status-filter-picker"),
                                                    value: serviceMappingStatusFilter,
                                                    options: serviceFilterOptions,
                                                    label: texts.mapServicesIsMapped(),
                                                    onChange: setServiceMappingStatusFilter
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TableList.Header, {
                                        items: getHeaderItems(accounting, texts)
                                    })
                                ]
                            }),
                            filteredServices === null || filteredServices === void 0 ? void 0 : filteredServices.map((serviceTemplate, index)=>{
                                const integratedId = serviceTemplateModel.integratedId({
                                    serviceTemplate,
                                    accounting
                                });
                                const integratedItem = integratedId ? accountingItemsDictionary[integratedId] : undefined;
                                const items = serviceTemplateModel.isBundle(serviceTemplate) ? (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || [] : serviceOnlyItems;
                                return /*#__PURE__*/ _jsx(MapServicesRow, {
                                    serviceTemplate: serviceTemplate,
                                    accountingItems: items,
                                    integratedItem: integratedItem,
                                    onChangeMap: onChangeMap,
                                    testId: "map-services-row-".concat(index),
                                    accounting: accounting
                                }, serviceTemplate.id);
                            })
                        ]
                    })
                ]
            })
        })
    });
}
const LoadingState = ()=>/*#__PURE__*/ _jsx("div", {
        className: styles.loading,
        children: /*#__PURE__*/ _jsx(Spinner, {
            size: 40,
            testId: "map-services-loading"
        })
    });
const EmptyState = (param)=>{
    let { accounting } = param;
    const { t: texts } = useLocaleKeys('integrations.accounting');
    const fullName = getAccountingDetails(accounting).fullName;
    return /*#__PURE__*/ _jsx(PageEmptyState, {
        testId: "map-services-empty-state",
        illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
        message: texts.mapServicesEmptyMessage({
            fullName
        }),
        actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
            text: texts.createNewAgreement(),
            testId: "map-service-new-btn"
        })
    });
};
const ErrorState = (param)=>{
    let { onRefresh } = param;
    return /*#__PURE__*/ _jsx(PageErrorState, {
        testId: "map-services-error-state",
        message: ErrorText.IntegrationDataRetrieval,
        onRefresh: onRefresh,
        skin: "secondary"
    });
};
